<template>
  <i-modal
    :title="$t(isEdit ? 'editing' : 'add', { value: $tc('apparel_gender', 1) })"
    :value="value"
    :load="loading"
    @submit="save"
    @input="$emit('input', $event)"
  >
    <div class="modal-body">
      <v-row class="my-0">
        <v-col cols="12">
          <ValidationProvider
            vid="name"
            :name="$tc('name', 1)"
            rules="required|min:2|max:64"
            v-slot="{ errors }"
          >
            <v-text-field
              autofocus
              name="name"
              :error-messages="errors[0]"
              v-model="form.name"
              outlined
              color="secondary"
              :label="$tc('name', 1)"
              :loading="loading"
              @keyup.enter="save"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
    </div>
  </i-modal>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'ApparelGenderModal',
  props: {
    /**
     * Controla la visibilidad del diálogo
     */
    value: {
      type: Boolean,
      required: true,
      default: false
    },
    /**
     * Objeto con los datos del género a editar (null para crear)
     */
    toEdit: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        name: ''
      },
      loading: false,
      isEdit: false
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/getCompanyData'
    }),
    companyPk() {
      return this.company ? this.company.pk || this.company.id : null
    }
  },
  watch: {
    /**
     * Cuando se recibe un elemento para editar, cargar los datos
     */
    toEdit: {
      handler(val) {
        this.setForm(val)
      },
      immediate: true
    },
    /**
     * Cuando se cierra el diálogo, resetear el formulario
     */
    value(val) {
      if (val) {
        this.setForm(this.toEdit)
      } else {
        this.form.name = ''
        this.isEdit = false
      }
    }
  },
  methods: {
    setForm(item) {
      this.isEdit = !!item
      this.form.name = item ? item.name || '' : ''
    },
    /**
     * Guardar el género (crear o actualizar)
     */
    async save() {
      if (this.loading) return

      // Validación manual antes de enviar
      if (!this.form.name || this.form.name.trim() === '') {
        this.$toast.error(this.$t('this_field_is_required'))
        return
      }

      if (this.form.name.length < 2) {
        this.$toast.error(this.$t('min_length', { count: 2 }))
        return
      }

      if (this.form.name.length > 64) {
        this.$toast.error(this.$t('max_length', { count: 64 }))
        return
      }
      this.loading = true
      try {
        const payload = {
          name: this.form.name.trim()
        }
        if (!this.isEdit && this.companyPk) {
          payload.company_id = this.companyPk
        }

        let response
        if (this.isEdit) {
          response = await this.$api.product.apparelGender.edit({
            pk: this.toEdit.id || this.toEdit.pk,
            form: payload
          })
          this.$toast.success(this.$t('updated_successfully'))
        } else {
          response = await this.$api.product.apparelGender.create({
            form: payload
          })
          this.$toast.success(this.$t('created_successfully'))
        }

        this.$emit('done', response.data)
        this.$emit('input', false)
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    },

    /**
     * Manejar errores de la API
     * @param {Object} error - Error de la petición
     */
    handleError(error) {
      // Error de duplicado (nombre ya existe)
      if (error.response?.status === 409) {
        this.$toast.error(this.$t('gender_name_already_exists'))
        return
      }

      // Error de integridad referencial (no se puede eliminar)
      if (error.response?.status === 400) {
        const message = error.response.data?.message || ''
        if (message.includes('integrit') || message.includes('foreign key')) {
          this.$toast.error(this.$t('cannot_delete_gender_in_use'))
          return
        }
      }

      // Error de validación del backend
      if (error.response?.data) {
        const backendErrors = error.response.data.errors || error.response.data
        if (backendErrors.name) {
          this.$toast.error(backendErrors.name[0])
          return
        }
        if (backendErrors.company) {
          this.$toast.error(backendErrors.company[0])
          return
        }
      }

      // Error de red o genérico
      if (error.message?.includes('Network Error')) {
        this.$toast.error(this.$t('network_error'))
      } else {
        this.$toast.error(this.$t('error_saving_gender'))
      }

      console.error('Error saving gender:', error)
    }
  }
}
</script>

<style scoped></style>
